import { createComponents } from 'labels/lib/createComponents'
import { replacePlaceholders } from 'labels/lib/replacePlaceholders'
import type { ReactNode } from 'react'

import type { CreateComponentsConfig } from '../../types'

export interface LabelProps extends CreateComponentsConfig {
	/** React children object with the string that will be translated */
	readonly children: ReactNode
	/** The config object used to replace placeholders */
	readonly placeholderValues?: Record<string | number, string | number>
}

/**
 * @example
 *  const translatedKey = 'Hola don <i>%1$s</i>, <a name=subscribe>subscribete aqui</a>'
 *  <Label
 *    placeholderValues={{
 *      1: Pepito
 *      subscribe: link-to-subscribe.html
 *    }}
 *    buttonProps={{
 *      1: {
 *      'data-testid': 'subscribe-button',
 *       onClick: () => console.log('subscribe clicked')
 * 		}
 *    }}
 *    italicProps={}
 *    boldProps={}
 *    linkProps={}
 *    paragraphProps={}
 *    underlineProps={}
 *    tableProps={}
 *    listProps={}
 *    containerProps={}
 *    sectionProps={}
 *    imageProps={}
 *  >
 *    {translatedKey}
 *  </Label>
 *
 * Entry point to create the translated label. Returns an element with the parsed literal with the placeholders replaced
 */
export const Label = ({
	children,
	placeholderValues,
	...config
}: LabelProps) => {
	if (typeof children !== 'string') {
		return null
	}

	const parsedLiteral = replacePlaceholders(children, placeholderValues)

	return createComponents(parsedLiteral, config)
}
